import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, projectFeedbackApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { CreateObjectionInput, JudgeEntryInput } from '../types/project-feedback.js';

/**
 * 見えない FB は API が 404 を返す。存在しないのと同じ扱いで、取り直しても変わらないので
 * 再試行しない（それ以外の失敗は既定どおり再試行する）。
 * @graph-connects ApiError [references] HTTP ステータスの判定
 */
function retryUnlessNotFound(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 404) return false;
  return failureCount < 3;
}

/**
 * 自分の案件 FB の一覧。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Fetch the signed-in engineer's own project feedback list
 * @graph-connects projectFeedbackApi.listMine [calls] 自分の FB 一覧 API
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useMyProjectFeedbacks() {
  return useQuery({
    queryKey: queryKeys.projectFeedback.mine(),
    queryFn: () => projectFeedbackApi.listMine(),
  });
}

/**
 * 見える範囲（評価者なら権限内のメンバー、本人の FB を含む）の案件 FB 一覧。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Fetch project feedback within the viewer's role scope for evaluators
 * @graph-connects projectFeedbackApi.list [calls] 範囲内の FB 一覧 API
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useProjectFeedbacks() {
  return useQuery({
    queryKey: queryKeys.projectFeedback.list(),
    queryFn: () => projectFeedbackApi.list(),
  });
}

/**
 * 案件 FB 1 件の詳細（本文・異議・判断・できること・判断の選択肢）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Fetch one project feedback with objections, judgments, permissions and verdict options
 * @graph-connects projectFeedbackApi.getDetail [calls] FB 詳細 API
 * @graph-connects retryUnlessNotFound [calls] 404 は再試行しない
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useProjectFeedbackDetail(feedbackId: string) {
  return useQuery({
    queryKey: queryKeys.projectFeedback.detail(feedbackId),
    queryFn: () => projectFeedbackApi.getDetail(feedbackId),
    retry: retryUnlessNotFound,
  });
}

/**
 * 案件 FB の全版（新しい順）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Fetch every saved version of a project feedback
 * @graph-connects projectFeedbackApi.listRevisions [calls] 過去の版 API
 * @graph-connects retryUnlessNotFound [calls] 404 は再試行しない
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function useProjectFeedbackRevisions(feedbackId: string) {
  return useQuery({
    queryKey: queryKeys.projectFeedback.revisions(feedbackId),
    queryFn: () => projectFeedbackApi.listRevisions(feedbackId),
    retry: retryUnlessNotFound,
  });
}

/**
 * 書き込みのあとに詳細と一覧（異議の数）を取り直す。
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
function useInvalidateFeedback(feedbackId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.projectFeedback.detail(feedbackId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.projectFeedback.list() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.projectFeedback.mine() }),
    ]);
  };
}

/**
 * 本人が項目に異議を出す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Let the feedback owner object to one entry and refresh the feedback views
 * @graph-connects projectFeedbackApi.createObjection [calls] 異議の作成 API
 * @graph-connects useInvalidateFeedback [calls] 詳細・一覧の取り直し
 */
export function useCreateFeedbackObjection(feedbackId: string) {
  const invalidate = useInvalidateFeedback(feedbackId);
  return useMutation({
    mutationFn: (input: CreateObjectionInput) =>
      projectFeedbackApi.createObjection(feedbackId, input),
    onSuccess: invalidate,
  });
}

/**
 * 本人が異議を取り下げる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Let the feedback owner withdraw an objection and refresh the feedback views
 * @graph-connects projectFeedbackApi.withdrawObjection [calls] 異議の取り下げ API
 * @graph-connects useInvalidateFeedback [calls] 詳細・一覧の取り直し
 */
export function useWithdrawFeedbackObjection(feedbackId: string) {
  const invalidate = useInvalidateFeedback(feedbackId);
  return useMutation({
    mutationFn: (objectionId: string) =>
      projectFeedbackApi.withdrawObjection(feedbackId, objectionId),
    onSuccess: invalidate,
  });
}

/**
 * 評価者が項目に判断を付ける・付け直す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business Let a scoped evaluator record a verdict on one entry and refresh the feedback views
 * @graph-connects projectFeedbackApi.judge [calls] 判断の作成・更新 API
 * @graph-connects useInvalidateFeedback [calls] 詳細・一覧の取り直し
 */
export function useJudgeFeedbackEntry(feedbackId: string) {
  const invalidate = useInvalidateFeedback(feedbackId);
  return useMutation({
    mutationFn: (input: JudgeEntryInput) => projectFeedbackApi.judge(feedbackId, input),
    onSuccess: invalidate,
  });
}
